import React from "react";
import { useTranslation } from "react-i18next";

interface HeaderProps {
  mobileMenuOpen: boolean;
  toggleMobileMenu: () => void;
}

const Header: React.FC<HeaderProps> = ({ mobileMenuOpen, toggleMobileMenu }) => {
  const { t, i18n } = useTranslation();

  const toggleLanguage = () => {
    const nextLang = i18n.language.startsWith("ko") ? "en" : "ko";
    i18n.changeLanguage(nextLang);
  };

  const isKorean = i18n.language.startsWith("ko");

  const navLinks = [
    { href: "#about",   label: t("nav.about")   },
    { href: "#history", label: t("nav.history")  },
    { href: "#product", label: t("nav.product")  },
    { href: "#contact", label: t("nav.contact")  },
  ];

  return (
    <header className="bg-white shadow-md fixed w-full top-0 z-50">
      <div className="container mx-auto px-5 py-2 md:py-3 flex justify-between items-center">
        {/* Logo */}
        <div className="logo">
          <img
            src="/images/neubreze-logo.png"
            alt="Neubreze Logo"
            className="h-3 md:h-10"
          />
        </div>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center space-x-8">
          <ul className="flex space-x-8">
            {navLinks.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  className="font-medium hover:text-blue-700 transition-colors"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>

          {/* Language Toggle Button */}
          <button
            onClick={toggleLanguage}
            className="flex items-center rounded-full border border-gray-200 bg-gray-50 p-0.5 my-1 min-h-[44px] text-xs font-bold hover:border-gray-300 transition-all duration-200 cursor-pointer"
            aria-label="Toggle language"
          >
            <span
              className={`px-2.5 py-1 rounded-full transition-all duration-200 ${
                isKorean
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-gray-400 hover:text-gray-600"
              }`}
            >
              KO
            </span>
            <span
              className={`px-2.5 py-1 rounded-full transition-all duration-200 ${
                !isKorean
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-gray-400 hover:text-gray-600"
              }`}
            >
              EN
            </span>
          </button>
        </nav>

        {/* Mobile: Lang Toggle + Hamburger */}
        <div className="lg:hidden flex items-center gap-3">
          <button
            onClick={toggleLanguage}
            className="flex items-center rounded-full border border-gray-200 bg-gray-50 p-0.5 my-1 min-h-[44px] text-xs font-bold hover:border-gray-300 transition-all duration-200 cursor-pointer"
            aria-label="Toggle language"
          >
            <span
              className={`px-2 py-1 rounded-full transition-all duration-200 ${
                isKorean
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-gray-400 hover:text-gray-600"
              }`}
            >
              KO
            </span>
            <span
              className={`px-2 py-1 rounded-full transition-all duration-200 ${
                !isKorean
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-gray-400 hover:text-gray-600"
              }`}
            >
              EN
            </span>
          </button>
          <button
            className="flex items-center justify-center w-11 h-11 -mr-2 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            onClick={toggleMobileMenu}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={mobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div id="mobile-menu" className="lg:hidden bg-white border-t">
          <ul className="flex flex-col">
            {navLinks.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  className="block py-3 px-5 hover:bg-gray-100 transition-colors"
                  onClick={toggleMobileMenu}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </header>
  );
};

export default Header;
